import { scaleColor, type LinearRGB } from '../../../core/color';
import type { PaletteModel } from '../../../data/types';
import { SURFACE } from '../../mesh/attributes';
import { SURFACE_TO_CLASS, computeShadeRatios, surfaceShade } from '../../../render/surfaceShading';

/**
 * The painting shows the city walls lit. Under the light measured on its round towers (STYLE §6.1)
 * the painter sees the walls' faces turned 97 to 118 deg, toward the light, at a wrapped Lambert of
 * 0.90: the median over the rendered painter view's wall pixels, whose projection also turns the
 * normals (the layout's faces alone give 0.92). There the stone's shade ratios brighten a colour
 * 1.44 times.
 */
export const PAINTER_WALL_LIGHT = 0.9;

/**
 * The walls' albedo takes the painter's light back out of the painted median, as water.frag takes
 * the painter's haze out of the water: the painter view shows the median, and free views shade
 * each face of a wall by the light.
 */
export function wallAlbedo(palette: PaletteModel): LinearRGB {
  const stone = computeShadeRatios(palette[SURFACE_TO_CLASS[SURFACE.stone]]);
  return scaleColor(palette.cityWall.median.linear, 1 / surfaceShade(stone, PAINTER_WALL_LIGHT));
}

/**
 * The inside of the niches under the wall's arches: the painting draws the openings of its arcade
 * as a row of dark marks (median #453308 along the Singel wall, 1.5 dE CIE76 from lineworkDark). The
 * niche backs face the painter as the wall faces do, so under the same light they show that colour.
 */
export function nicheAlbedo(palette: PaletteModel): LinearRGB {
  const stone = computeShadeRatios(palette[SURFACE_TO_CLASS[SURFACE.stone]]);
  return scaleColor(
    palette.lineworkDark.median.linear,
    1 / surfaceShade(stone, PAINTER_WALL_LIGHT)
  );
}
